// Per-client landing screen. Shown at /clients/:clientId before any specific
// scenario has been picked. Renders two sortable-table sections:
//
//   1. Scenarios — saved sizing scenarios + "new" affordance.
//   2. Staff plans — saved Current Staff Plans + "add" affordance.
//
// Visual chrome mirrors the Client Workspace landing table (ClientList) so
// the whole workspace reads as one consistent table experience.

import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowRight,
  Copy,
  DollarSign,
  FileText,
  Globe2,
  Plus,
  Trash2,
  Users,
} from 'lucide-react';
import clsx from 'clsx';
import type { Client, Scenario, SpendSet, StaffPlan } from '@rc/engine/types';
import type { ModelSummary } from '../lib/api';
import {
  isActualStaffPlan,
  modelCurrency,
  staffPlanHorizonWords,
  totalsForStaffPlan,
} from '@rc/engine';
import { fmtMoney } from '../lib/format';
import { COUNTRIES } from './intake/CountryPicker';
import { SortHeader, formatRelative, type SortDir } from './tables/SortHeader';
import { withGeoclientQuery } from '../lib/geoclientContext';

const COUNTRY_NAME = new Map(COUNTRIES.map((c) => [c.code, c.name]));
function countryLabel(code: string): string {
  return COUNTRY_NAME.get(code) || code;
}

type StaffPlanListItem = StaffPlan & {
  metadata?: {
    totalFte?: number;
    onFte?: number;
    hubFte?: number;
    cells?: number;
  };
};

interface Props {
  client: Client;
  /** Full model catalog. Each scenario row resolves its own model name from
   *  this list (since the client itself no longer pins a model). */
  models: ModelSummary[];
  scenarios: Scenario[];
  staffPlans: StaffPlanListItem[];
  spendSets: SpendSet[];
  canDelete: boolean;
  geoclientId?: string;
  onNewScenario: () => void;
  onCloneScenario: (s: Scenario) => void;
  onDeleteScenario: (s: Scenario) => void;
  onDeleteStaffPlan: (p: StaffPlan) => void;
  onDeleteSpendSet: (s: SpendSet) => void;
}

export default function ClientLanding({
  client,
  models,
  scenarios,
  staffPlans,
  spendSets,
  canDelete,
  geoclientId,
  onNewScenario,
  onCloneScenario,
  onDeleteScenario,
  onDeleteStaffPlan,
  onDeleteSpendSet,
}: Props) {
  return (
    <div className="space-y-8">
      <ScenariosTable
        client={client}
        models={models}
        scenarios={scenarios}
        canDelete={canDelete}
        geoclientId={geoclientId}
        onNewScenario={onNewScenario}
        onClone={onCloneScenario}
        onDelete={onDeleteScenario}
      />
      <StaffPlansTable
        client={client}
        plans={staffPlans}
        canDelete={canDelete}
        geoclientId={geoclientId}
        onDelete={onDeleteStaffPlan}
      />
      <SpendSetsTable
        client={client}
        spendSets={spendSets}
        models={models}
        canDelete={canDelete}
        geoclientId={geoclientId}
        onDelete={onDeleteSpendSet}
      />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Section header (shared chrome for both tables)
// ---------------------------------------------------------------------------

function SectionHeader({
  title,
  description,
  icon,
  action,
}: {
  title: string;
  description: string;
  icon: React.ReactNode;
  action?: React.ReactNode;
}) {
  return (
    <div className="flex items-end justify-between gap-3 mb-3">
      <div className="flex items-start gap-3">
        <div className="w-9 h-9 rounded-lg bg-slate-100 flex items-center justify-center shrink-0 mt-0.5">
          {icon}
        </div>
        <div>
          <h2 className="text-base font-semibold text-slate-900">{title}</h2>
          <p className="text-xs text-slate-500 max-w-lg">{description}</p>
        </div>
      </div>
      {action}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Scenarios
// ---------------------------------------------------------------------------

type ScenarioSortKey =
  | 'name'
  | 'budget'
  | 'fte'
  | 'markets'
  | 'updated';

interface ScenarioRow {
  scenario: Scenario;
  isLatest: boolean;
  budget: number;
  fte: number;
  markets: string[];
  updatedMs: number;
}

function buildScenarioRows(scenarios: Scenario[], latestId?: string): ScenarioRow[] {
  return scenarios.map((s) => {
    const set = new Set<string>();
    for (const code of s.profile?.scenarioMarkets || []) if (code) set.add(code);
    for (const pair of s.profile?.geoModel?.pairs || []) {
      for (const code of pair.activationMarkets || []) if (code) set.add(code);
    }
    return {
      scenario: s,
      isLatest: s.id === latestId,
      budget: Number(s.profile?.totalBudget) || 0,
      fte: Number(s.result?.summary?.totalBaseFte) || 0,
      markets: Array.from(set).sort((a, b) =>
        countryLabel(a).localeCompare(countryLabel(b)),
      ),
      updatedMs: new Date(s.updatedAt || s.createdAt || 0).getTime(),
    };
  });
}

function compareScenarios(
  a: ScenarioRow,
  b: ScenarioRow,
  key: ScenarioSortKey,
  dir: SortDir,
): number {
  const sign = dir === 'asc' ? 1 : -1;
  const tie = a.scenario.name.localeCompare(b.scenario.name);
  switch (key) {
    case 'name':
      return sign * a.scenario.name.localeCompare(b.scenario.name);
    case 'budget': {
      const d = a.budget - b.budget;
      return d !== 0 ? sign * d : tie;
    }
    case 'fte': {
      const d = a.fte - b.fte;
      return d !== 0 ? sign * d : tie;
    }
    case 'markets': {
      const d = a.markets.length - b.markets.length;
      return d !== 0 ? sign * d : tie;
    }
    case 'updated': {
      const d = a.updatedMs - b.updatedMs;
      return d !== 0 ? sign * d : tie;
    }
  }
}

function ScenariosTable({
  client,
  models,
  scenarios,
  canDelete,
  geoclientId,
  onNewScenario,
  onClone,
  onDelete,
}: {
  client: Client;
  models: ModelSummary[];
  scenarios: Scenario[];
  canDelete: boolean;
  geoclientId?: string;
  onNewScenario: () => void;
  onClone: (s: Scenario) => void;
  onDelete: (s: Scenario) => void;
}) {
  const [sortKey, setSortKey] = useState<ScenarioSortKey>('updated');
  const [sortDir, setSortDir] = useState<SortDir>('desc');

  const rows = useMemo(
    () => buildScenarioRows(scenarios, client.latestScenarioId),
    [scenarios, client.latestScenarioId],
  );
  const sorted = useMemo(
    () => [...rows].sort((a, b) => compareScenarios(a, b, sortKey, sortDir)),
    [rows, sortKey, sortDir],
  );

  const toggleSort = (key: ScenarioSortKey) => {
    if (key === sortKey) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(key);
      setSortDir(
        key === 'updated' || key === 'budget' || key === 'fte' || key === 'markets'
          ? 'desc'
          : 'asc',
      );
    }
  };

  const canDeleteScenario = canDelete;
  const empty = scenarios.length === 0;

  return (
    <section>
      <SectionHeader
        title="Scenarios"
        description="Saved sizing scenarios for this client. Open one to edit inputs and view modeled FTE."
        icon={<FileText size={16} className="text-brand-blue-500" />}
        action={
          !empty && (
            <button onClick={onNewScenario} className="btn-primary">
              <Plus size={14} /> New scenario
            </button>
          )
        }
      />
      {empty ? (
        <EmptyState
          icon={<FileText size={20} className="text-slate-400" />}
          title="No scenarios yet"
          description="Create a scenario to start sizing this client."
          cta={
            <button onClick={onNewScenario} className="btn-primary">
              <Plus size={14} /> New scenario
            </button>
          }
        />
      ) : (
        <div className="card overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-slate-50/60 border-b border-slate-200 text-left">
                  <SortHeader<ScenarioSortKey>
                    label="Scenario"
                    sortKey="name"
                    activeKey={sortKey}
                    dir={sortDir}
                    onSort={toggleSort}
                    className="pl-4 pr-3"
                  />
                  <SortHeader<ScenarioSortKey>
                    label="Markets"
                    sortKey="markets"
                    activeKey={sortKey}
                    dir={sortDir}
                    onSort={toggleSort}
                    className="px-3"
                  />
                  <SortHeader<ScenarioSortKey>
                    label="Budget"
                    sortKey="budget"
                    activeKey={sortKey}
                    dir={sortDir}
                    onSort={toggleSort}
                    className="px-3 text-right"
                    numeric
                  />
                  <SortHeader<ScenarioSortKey>
                    label="Modeled FTE"
                    sortKey="fte"
                    activeKey={sortKey}
                    dir={sortDir}
                    onSort={toggleSort}
                    className="px-3 text-right"
                    numeric
                  />
                  <th className="py-2.5 px-3 text-[11px] uppercase tracking-wider font-semibold text-slate-500">
                    Model
                  </th>
                  <SortHeader<ScenarioSortKey>
                    label="Updated"
                    sortKey="updated"
                    activeKey={sortKey}
                    dir={sortDir}
                    onSort={toggleSort}
                    className="px-3"
                  />
                  <th className="py-2.5 px-3 text-[11px] uppercase tracking-wider font-semibold text-slate-500">
                    Creator
                  </th>
                  <th className="pr-4 pl-3 py-2.5 text-right text-[11px] uppercase tracking-wider font-semibold text-slate-500">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody>
                {sorted.map((row, i) => (
                  <ScenarioRow
                    key={row.scenario.id}
                    row={row}
                    client={client}
                    models={models}
                    geoclientId={geoclientId}
                    canDelete={canDeleteScenario}
                    onClone={() => onClone(row.scenario)}
                    onDelete={() => onDelete(row.scenario)}
                    stripe={i % 2 === 1}
                  />
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </section>
  );
}

function ScenarioRow({
  row,
  client,
  models,
  geoclientId,
  canDelete,
  onClone,
  onDelete,
  stripe,
}: {
  row: ScenarioRow;
  client: Client;
  models: ModelSummary[];
  geoclientId?: string;
  canDelete: boolean;
  onClone: () => void;
  onDelete: () => void;
  stripe: boolean;
}) {
  const { scenario: s, isLatest, budget, fte, markets, updatedMs } = row;
  // Resolve the scenario's own model — different scenarios under one client
  // can run against different models so we can't lift this out of the row.
  const scenarioModel = models.find((m) => m.id === s.modelId);
  return (
    <tr
      className={clsx(
        'border-b border-slate-100 hover:bg-brand-blue-50/30 transition-colors',
        stripe && 'bg-slate-50/30',
      )}
    >
      <td className="py-2.5 pl-4 pr-3 align-middle">
        <Link
          to={withGeoclientQuery(`/clients/${client.id}/scenarios/${s.id}`, geoclientId)}
          className="group inline-flex items-center gap-2"
        >
          <span className="font-medium text-slate-900 group-hover:text-brand-blue-700">
            {s.name}
          </span>
          {isLatest && <span className="pill-blue text-[10px]">latest</span>}
          <ArrowRight
            size={13}
            className="text-slate-300 group-hover:text-brand-blue-500 transition-colors"
          />
        </Link>
        {s.notes && (
          <div className="text-[11px] text-slate-500 mt-0.5 truncate max-w-md">
            {s.notes}
          </div>
        )}
        <div className="text-[10px] font-mono text-slate-400 mt-0.5">ID: {s.id}</div>
      </td>
      <td className="py-2.5 px-3 align-middle">
        <MarketsCell markets={markets} />
      </td>
      <td className="py-2.5 px-3 align-middle text-right tabular-nums text-slate-700">
        {budget > 0 ? (
          fmtMoney(budget, modelCurrency(scenarioModel).base)
        ) : (
          <span className="text-slate-400">—</span>
        )}
      </td>
      <td className="py-2.5 px-3 align-middle text-right tabular-nums text-slate-700">
        {fte > 0 ? fte.toFixed(1) : <span className="text-slate-400">—</span>}
      </td>
      <td className="py-2.5 px-3 align-middle text-slate-600 text-xs whitespace-nowrap">
        {scenarioModel?.name || s.modelId}
      </td>
      <td className="py-2.5 px-3 align-middle text-slate-600 text-xs whitespace-nowrap">
        {formatRelative(updatedMs)}
      </td>
      <td className="py-2.5 px-3 align-middle text-slate-600 text-xs max-w-[180px] truncate" title={s.createdBy?.email || ''}>
        {s.createdBy?.email || <span className="text-slate-400">—</span>}
      </td>
      <td className="py-2.5 pl-3 pr-4 align-middle text-right whitespace-nowrap">
        <button
          type="button"
          onClick={onClone}
          className="text-xs inline-flex items-center gap-1 px-2 py-1 rounded-md border border-slate-200 hover:border-brand-purple-300 hover:bg-brand-purple-50 hover:text-brand-purple-700 text-slate-600 font-medium mr-1"
          title="Clone scenario"
        >
          <Copy size={11} /> Clone
        </button>
        {canDelete && (
          <button
            type="button"
            onClick={onDelete}
            className="text-xs inline-flex items-center gap-1 px-2 py-1 rounded-md border border-slate-200 hover:border-rose-300 hover:bg-rose-50 hover:text-rose-700 text-slate-600 font-medium"
            title="Delete scenario"
          >
            <Trash2 size={11} /> Delete
          </button>
        )}
      </td>
    </tr>
  );
}

function MarketsCell({ markets }: { markets: string[] }) {
  if (markets.length === 0) {
    return (
      <span className="inline-flex items-center gap-1 text-xs text-slate-400">
        <Globe2 size={11} />
        None
      </span>
    );
  }
  const visible = markets.slice(0, 4);
  const overflow = markets.length - visible.length;
  return (
    <div className="flex items-center flex-wrap gap-1">
      {visible.map((code) => (
        <span
          key={code}
          className="inline-flex items-center px-1.5 py-0.5 rounded-md bg-slate-100 text-slate-700 text-[11px] font-medium tabular-nums"
          title={countryLabel(code)}
        >
          {code}
        </span>
      ))}
      {overflow > 0 && (
        <span
          className="text-[11px] text-slate-500"
          title={markets.slice(4).map(countryLabel).join(', ')}
        >
          +{overflow}
        </span>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Staff plans
// ---------------------------------------------------------------------------

type PlanSortKey =
  | 'name'
  | 'source'
  | 'hub'
  | 'total'
  | 'on'
  | 'hubFte'
  | 'cells'
  | 'updated';

/** Whose numbers a plan holds. Only a manual plan records the team a client
 *  really has, which is why a scenario can attach one and not the other. */
type PlanSourceFilter = 'all' | 'actual' | 'modeled' | 'final';

function matchesSourceFilter(
  plan: StaffPlanListItem,
  filter: PlanSourceFilter,
): boolean {
  if (filter === 'all') return true;
  if (filter === 'actual') return isActualStaffPlan(plan);
  return plan.kind === filter;
}

interface PlanRow {
  plan: StaffPlanListItem;
  total: number;
  on: number;
  hub: number;
  cells: number;
  updatedMs: number;
}

function buildPlanRows(plans: StaffPlanListItem[]): PlanRow[] {
  return plans.map((p) => {
    const totals = totalsForStaffPlan(p);
    return {
      plan: p,
      total: p.metadata?.totalFte ?? totals.total,
      on: p.metadata?.onFte ?? totals.onTotal,
      hub: p.metadata?.hubFte ?? totals.hubTotal,
      cells: p.metadata?.cells ?? (p.cells || []).length,
      updatedMs: new Date(p.updatedAt || p.createdAt || 0).getTime(),
    };
  });
}

function comparePlans(
  a: PlanRow,
  b: PlanRow,
  key: PlanSortKey,
  dir: SortDir,
): number {
  const sign = dir === 'asc' ? 1 : -1;
  const tie = a.plan.name.localeCompare(b.plan.name);
  switch (key) {
    case 'name':
      return sign * a.plan.name.localeCompare(b.plan.name);
    case 'source': {
      const rank = (r: PlanRow) =>
        isActualStaffPlan(r.plan) ? 0 : r.plan.kind === 'modeled' ? 1 : 2;
      return sign * (rank(a) - rank(b)) || tie;
    }
    case 'hub': {
      const al = a.plan.hubLocation?.label || '';
      const bl = b.plan.hubLocation?.label || '';
      return sign * al.localeCompare(bl) || tie;
    }
    case 'total': {
      const d = a.total - b.total;
      return d !== 0 ? sign * d : tie;
    }
    case 'on': {
      const d = a.on - b.on;
      return d !== 0 ? sign * d : tie;
    }
    case 'hubFte': {
      const d = a.hub - b.hub;
      return d !== 0 ? sign * d : tie;
    }
    case 'cells': {
      const d = a.cells - b.cells;
      return d !== 0 ? sign * d : tie;
    }
    case 'updated': {
      const d = a.updatedMs - b.updatedMs;
      return d !== 0 ? sign * d : tie;
    }
  }
}

function StaffPlansTable({
  client,
  plans,
  canDelete,
  geoclientId,
  onDelete,
}: {
  client: Client;
  plans: StaffPlanListItem[];
  canDelete: boolean;
  geoclientId?: string;
  onDelete: (p: StaffPlan) => void;
}) {
  const [sortKey, setSortKey] = useState<PlanSortKey>('updated');
  const [sortDir, setSortDir] = useState<SortDir>('desc');
  const [source, setSource] = useState<PlanSourceFilter>('all');

  const rows = useMemo(() => buildPlanRows(plans), [plans]);
  const generatedCount = useMemo(
    () => rows.filter((r) => !isActualStaffPlan(r.plan)).length,
    [rows],
  );
  const finalCount = useMemo(
    () => rows.filter((r) => r.plan.kind === 'final').length,
    [rows],
  );
  const sorted = useMemo(() => {
    const kept = rows.filter((r) => matchesSourceFilter(r.plan, source));
    return [...kept].sort((a, b) => comparePlans(a, b, sortKey, sortDir));
  }, [rows, sortKey, sortDir, source]);

  const toggleSort = (key: PlanSortKey) => {
    if (key === sortKey) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(key);
      setSortDir(
        key === 'updated' ||
          key === 'total' ||
          key === 'on' ||
          key === 'hubFte' ||
          key === 'cells'
          ? 'desc'
          : 'asc',
      );
    }
  };

  const empty = plans.length === 0;

  return (
    <section>
      <SectionHeader
        title="Staff plans"
        description="Snapshots of the team you have today. Used to compare modeled vs current."
        icon={<Users size={16} className="text-brand-purple-500" />}
        action={
          !empty && (
            <Link to={withGeoclientQuery(`/clients/${client.id}/staff-plans/new`, geoclientId)} className="btn-primary">
              <Plus size={14} /> New staff plan
            </Link>
          )
        }
      />
      {empty ? (
        <EmptyState
          icon={<Users size={20} className="text-slate-400" />}
          title="No current staff plan yet"
          description="Add a plan to capture the team this client has today, by Discipline × Channel × Level."
          cta={
            <Link to={withGeoclientQuery(`/clients/${client.id}/staff-plans/new`, geoclientId)} className="btn-primary">
              <Plus size={14} /> Add current staff plan
            </Link>
          }
        />
      ) : (
        <div className="card overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-slate-50/60 border-b border-slate-200 text-left">
                  <SortHeader<PlanSortKey>
                    label="Plan"
                    sortKey="name"
                    activeKey={sortKey}
                    dir={sortDir}
                    onSort={toggleSort}
                    className="pl-4 pr-3"
                  />
                  <SortHeader<PlanSortKey>
                    label="Source"
                    sortKey="source"
                    activeKey={sortKey}
                    dir={sortDir}
                    onSort={toggleSort}
                    className="px-3"
                  />
                  <SortHeader<PlanSortKey>
                    label="Hub location"
                    sortKey="hub"
                    activeKey={sortKey}
                    dir={sortDir}
                    onSort={toggleSort}
                    className="px-3"
                  />
                  <SortHeader<PlanSortKey>
                    label="Total FTE"
                    sortKey="total"
                    activeKey={sortKey}
                    dir={sortDir}
                    onSort={toggleSort}
                    className="px-3 text-right"
                    numeric
                  />
                  <SortHeader<PlanSortKey>
                    label="On"
                    sortKey="on"
                    activeKey={sortKey}
                    dir={sortDir}
                    onSort={toggleSort}
                    className="px-3 text-right"
                    numeric
                  />
                  <SortHeader<PlanSortKey>
                    label="Hub"
                    sortKey="hubFte"
                    activeKey={sortKey}
                    dir={sortDir}
                    onSort={toggleSort}
                    className="px-3 text-right"
                    numeric
                  />
                  <SortHeader<PlanSortKey>
                    label="Cells"
                    sortKey="cells"
                    activeKey={sortKey}
                    dir={sortDir}
                    onSort={toggleSort}
                    className="px-3 text-right"
                    numeric
                  />
                  <SortHeader<PlanSortKey>
                    label="Updated"
                    sortKey="updated"
                    activeKey={sortKey}
                    dir={sortDir}
                    onSort={toggleSort}
                    className="px-3"
                  />
                  <th className="py-2.5 px-3 text-[11px] uppercase tracking-wider font-semibold text-slate-500">
                    Creator
                  </th>
                  <th className="pr-4 pl-3 py-2.5 text-right text-[11px] uppercase tracking-wider font-semibold text-slate-500">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody>
                {sorted.map((row, i) => (
                  <PlanRow
                    key={row.plan.id}
                    row={row}
                    client={client}
                    geoclientId={geoclientId}
                    canDelete={canDelete}
                    onDelete={() => onDelete(row.plan)}
                    stripe={i % 2 === 1}
                  />
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </section>
  );
}

/** Says where a plan's numbers came from, and for a generated one links back to
 *  the scenario that produced it. A plan tagged this way cannot serve as a
 *  scenario's Actual team, so the distinction is worth showing plainly. */
function PlanSourceCell({
  plan,
  client,
}: {
  plan: StaffPlanListItem;
  client: Client;
}) {
  if (isActualStaffPlan(plan)) {
    return (
      <span className="pill text-[10px] bg-slate-100 text-slate-600">
        Manual input
      </span>
    );
  }
  const adjusted = plan.source?.adjustedCellCount || 0;
  const isFinal = plan.kind === 'final';
  return (
    <div className="min-w-0">
      <span
        className={clsx(
          'pill text-[10px]',
          isFinal
            ? 'bg-emerald-50 text-emerald-700'
            : 'bg-brand-purple-50 text-brand-purple-700',
        )}
      >
        {isFinal ? 'Final' : adjusted > 0 ? 'Model & Adjusted' : 'Model'}
      </span>
      {isFinal && (
        <div className="text-[10px] text-slate-500 mt-0.5">
          at {staffPlanHorizonWords(plan.source?.horizon)}
        </div>
      )}
      {plan.source?.scenarioId && (
        <div className="text-[11px] text-slate-500 mt-0.5 truncate max-w-[180px]">
          from{' '}
          <Link
            to={`/clients/${client.id}/scenarios/${plan.source.scenarioId}`}
            className="text-brand-purple-600 hover:text-brand-purple-800 hover:underline"
          >
            {plan.source.scenarioName || 'scenario'}
          </Link>
        </div>
      )}
      {adjusted > 0 && (
        <div className="text-[10px] text-slate-400">
          {adjusted} cell{adjusted === 1 ? '' : 's'} adjusted
        </div>
      )}
    </div>
  );
}

function PlanRow({
  row,
  client,
  geoclientId,
  canDelete,
  onDelete,
  stripe,
}: {
  row: PlanRow;
  client: Client;
  geoclientId?: string;
  canDelete: boolean;
  onDelete: () => void;
  stripe: boolean;
}) {
  const { plan, total, on, hub, cells, updatedMs } = row;
  return (
    <tr
      className={clsx(
        'border-b border-slate-100 hover:bg-brand-purple-50/30 transition-colors',
        stripe && 'bg-slate-50/30',
      )}
    >
      <td className="py-2.5 pl-4 pr-3 align-middle">
        <Link
          to={withGeoclientQuery(`/clients/${client.id}/staff-plans/${plan.id}`, geoclientId)}
          className="group inline-flex items-center gap-2"
        >
          <span className="font-medium text-slate-900 group-hover:text-brand-purple-700">
            {plan.name}
          </span>
          <ArrowRight
            size={13}
            className="text-slate-300 group-hover:text-brand-purple-500 transition-colors"
          />
        </Link>
        {plan.notes && (
          <div className="text-[11px] text-slate-500 mt-0.5 truncate max-w-md">
            {plan.notes}
          </div>
        )}
        <div className="text-[10px] font-mono text-slate-400 mt-0.5">ID: {plan.id}</div>
      </td>
      <td className="py-2.5 px-3 align-middle">
        <PlanSourceCell plan={plan} client={client} />
      </td>
      <td className="py-2.5 px-3 align-middle">
        {plan.hubLocation ? (
          <div className="inline-flex items-center gap-1.5">
            <span className="text-slate-700 text-sm">
              {plan.hubLocation.label || '—'}
            </span>
            <span
              className={clsx(
                'pill text-[10px]',
                plan.hubLocation.shore === 'nearshore'
                  ? 'bg-emerald-50 text-emerald-700'
                  : 'bg-amber-50 text-amber-800',
              )}
            >
              {plan.hubLocation.shore}
            </span>
          </div>
        ) : (
          <span className="text-xs text-slate-400">Not set</span>
        )}
      </td>
      <td className="py-2.5 px-3 align-middle text-right tabular-nums text-slate-800 font-medium">
        {total.toFixed(1)}
      </td>
      <td className="py-2.5 px-3 align-middle text-right tabular-nums text-slate-700">
        {on.toFixed(1)}
      </td>
      <td className="py-2.5 px-3 align-middle text-right tabular-nums text-amber-800">
        {hub.toFixed(1)}
      </td>
      <td className="py-2.5 px-3 align-middle text-right tabular-nums text-slate-600">
        {cells}
      </td>
      <td className="py-2.5 px-3 align-middle text-slate-600 text-xs whitespace-nowrap">
        {formatRelative(updatedMs)}
      </td>
      <td
        className="py-2.5 px-3 align-middle text-slate-600 text-xs max-w-[180px] truncate"
        title={plan.createdBy?.email || ''}
      >
        {plan.createdBy?.email || <span className="text-slate-400">—</span>}
      </td>
      <td className="py-2.5 pl-3 pr-4 align-middle text-right whitespace-nowrap">
        {canDelete && (
          <button
            type="button"
            onClick={onDelete}
            className="text-xs inline-flex items-center gap-1 px-2 py-1 rounded-md border border-slate-200 hover:border-rose-300 hover:bg-rose-50 hover:text-rose-700 text-slate-600 font-medium"
            title="Delete plan"
          >
            <Trash2 size={11} /> Delete
          </button>
        )}
      </td>
    </tr>
  );
}

// ---------------------------------------------------------------------------
// Spend sets
// ---------------------------------------------------------------------------

type SpendSetSortKey = 'name' | 'budget' | 'preset' | 'updated' | 'savedBy';

interface SpendSetRowData {
  set: SpendSet;
  updatedMs: number;
}

function buildSpendSetRows(spendSets: SpendSet[]): SpendSetRowData[] {
  return spendSets.map((s) => ({
    set: s,
    updatedMs: new Date(s.updatedAt || s.createdAt || 0).getTime(),
  }));
}

function compareSpendSets(
  a: SpendSetRowData,
  b: SpendSetRowData,
  key: SpendSetSortKey,
  dir: SortDir,
): number {
  const sign = dir === 'asc' ? 1 : -1;
  const tie = a.set.name.localeCompare(b.set.name);
  switch (key) {
    case 'name':
      return sign * a.set.name.localeCompare(b.set.name);
    case 'budget': {
      const d = (a.set.totalBudget || 0) - (b.set.totalBudget || 0);
      return d !== 0 ? sign * d : tie;
    }
    case 'preset': {
      const ap = a.set.channelMixPresetId || '';
      const bp = b.set.channelMixPresetId || '';
      return sign * ap.localeCompare(bp) || tie;
    }
    case 'savedBy': {
      const an = a.set.updatedBy?.email || a.set.createdBy?.email || '';
      const bn = b.set.updatedBy?.email || b.set.createdBy?.email || '';
      return sign * an.localeCompare(bn) || tie;
    }
    case 'updated': {
      const d = a.updatedMs - b.updatedMs;
      return d !== 0 ? sign * d : tie;
    }
  }
}

function SpendSetsTable({
  client,
  spendSets,
  models,
  canDelete,
  geoclientId,
  onDelete,
}: {
  client: Client;
  spendSets: SpendSet[];
  models: ModelSummary[];
  canDelete: boolean;
  geoclientId?: string;
  onDelete: (s: SpendSet) => void;
}) {
  const [sortKey, setSortKey] = useState<SpendSetSortKey>('updated');
  const [sortDir, setSortDir] = useState<SortDir>('desc');

  const rows = useMemo(() => buildSpendSetRows(spendSets), [spendSets]);
  const sorted = useMemo(
    () => [...rows].sort((a, b) => compareSpendSets(a, b, sortKey, sortDir)),
    [rows, sortKey, sortDir],
  );

  const toggleSort = (key: SpendSetSortKey) => {
    if (key === sortKey) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(key);
      setSortDir(key === 'budget' || key === 'updated' ? 'desc' : 'asc');
    }
  };

  const empty = spendSets.length === 0;

  return (
    <section>
      <SectionHeader
        title="Spend sets"
        description="Saved channel spend configurations. Load one into a scenario to populate the media budget and channel mix."
        icon={<DollarSign size={16} className="text-emerald-600" />}
        action={
          !empty && (
            <Link to={withGeoclientQuery(`/clients/${client.id}/spend-sets/new`, geoclientId)} className="btn-primary">
              <Plus size={14} /> New spend set
            </Link>
          )
        }
      />
      {empty ? (
        <EmptyState
          icon={<DollarSign size={20} className="text-slate-400" />}
          title="No spend sets yet"
          description="Save a channel spend configuration to reuse it across multiple scenarios."
          cta={
            <Link to={withGeoclientQuery(`/clients/${client.id}/spend-sets/new`, geoclientId)} className="btn-primary">
              <Plus size={14} /> New spend set
            </Link>
          }
        />
      ) : (
        <div className="card overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-slate-50/60 border-b border-slate-200 text-left">
                  <SortHeader<SpendSetSortKey>
                    label="Name"
                    sortKey="name"
                    activeKey={sortKey}
                    dir={sortDir}
                    onSort={toggleSort}
                    className="pl-4 pr-3"
                  />
                  <SortHeader<SpendSetSortKey>
                    label="Total budget"
                    sortKey="budget"
                    activeKey={sortKey}
                    dir={sortDir}
                    onSort={toggleSort}
                    className="px-3 text-right"
                    numeric
                  />
                  <SortHeader<SpendSetSortKey>
                    label="Preset"
                    sortKey="preset"
                    activeKey={sortKey}
                    dir={sortDir}
                    onSort={toggleSort}
                    className="px-3"
                  />
                  <SortHeader<SpendSetSortKey>
                    label="Last saved"
                    sortKey="updated"
                    activeKey={sortKey}
                    dir={sortDir}
                    onSort={toggleSort}
                    className="px-3"
                  />
                  <SortHeader<SpendSetSortKey>
                    label="Saved by"
                    sortKey="savedBy"
                    activeKey={sortKey}
                    dir={sortDir}
                    onSort={toggleSort}
                    className="px-3"
                  />
                  <th className="pr-4 pl-3 py-2.5 text-right text-[11px] uppercase tracking-wider font-semibold text-slate-500">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody>
                {sorted.map((row, i) => (
                  <SpendSetRow
                    key={row.set.id}
                    row={row}
                    client={client}
                    models={models}
                    geoclientId={geoclientId}
                    canDelete={canDelete}
                    onDelete={() => onDelete(row.set)}
                    stripe={i % 2 === 1}
                  />
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </section>
  );
}

function SpendSetRow({
  row,
  client,
  models,
  geoclientId,
  canDelete,
  onDelete,
  stripe,
}: {
  row: SpendSetRowData;
  client: Client;
  models: ModelSummary[];
  geoclientId?: string;
  canDelete: boolean;
  onDelete: () => void;
  stripe: boolean;
}) {
  const { set, updatedMs } = row;
  const savedBy = set.updatedBy?.email || set.createdBy?.email;
  // Spend sets are bound to a model, so they inherit its currency.
  const baseCurrency = modelCurrency(models.find((m) => m.id === set.modelId)).base;
  return (
    <tr
      className={clsx(
        'border-b border-slate-100 hover:bg-emerald-50/30 transition-colors',
        stripe && 'bg-slate-50/30',
      )}
    >
      <td className="py-2.5 pl-4 pr-3 align-middle">
        <Link
          to={withGeoclientQuery(`/clients/${client.id}/spend-sets/${set.id}`, geoclientId)}
          className="group inline-flex items-center gap-2"
        >
          <span className="font-medium text-slate-900 group-hover:text-emerald-700">
            {set.name}
          </span>
          <ArrowRight
            size={13}
            className="text-slate-300 group-hover:text-emerald-500 transition-colors"
          />
        </Link>
        {set.notes && (
          <div className="text-[11px] text-slate-500 mt-0.5 truncate max-w-md">{set.notes}</div>
        )}
        <div className="text-[10px] font-mono text-slate-400 mt-0.5">ID: {set.id}</div>
      </td>
      <td className="py-2.5 px-3 align-middle text-right tabular-nums text-slate-700">
        {set.totalBudget > 0 ? (
          fmtMoney(set.totalBudget, baseCurrency)
        ) : (
          <span className="text-slate-400">—</span>
        )}
      </td>
      <td className="py-2.5 px-3 align-middle text-slate-600 text-xs">
        {set.channelMixPresetId && set.channelMixPresetId !== 'custom'
          ? set.channelMixPresetId
          : <span className="text-slate-400">Custom</span>}
      </td>
      <td className="py-2.5 px-3 align-middle text-slate-600 text-xs whitespace-nowrap">
        {formatRelative(updatedMs)}
      </td>
      <td className="py-2.5 px-3 align-middle text-slate-600 text-xs whitespace-nowrap">
        {savedBy || <span className="text-slate-400">—</span>}
      </td>
      <td className="py-2.5 pl-3 pr-4 align-middle text-right whitespace-nowrap">
        {canDelete && (
          <button
            type="button"
            onClick={onDelete}
            className="text-xs inline-flex items-center gap-1 px-2 py-1 rounded-md border border-slate-200 hover:border-rose-300 hover:bg-rose-50 hover:text-rose-700 text-slate-600 font-medium"
            title="Delete spend set"
          >
            <Trash2 size={11} /> Delete
          </button>
        )}
      </td>
    </tr>
  );
}

// ---------------------------------------------------------------------------
// Empty state (shared)
// ---------------------------------------------------------------------------

function EmptyState({
  icon,
  title,
  description,
  cta,
}: {
  icon: React.ReactNode;
  title: string;
  description: string;
  cta?: React.ReactNode;
}) {
  return (
    <div className="card card-body text-center py-10">
      <div className="w-10 h-10 rounded-xl bg-slate-50 flex items-center justify-center mx-auto mb-3">
        {icon}
      </div>
      <h3 className="font-semibold text-slate-900 text-sm">{title}</h3>
      <p className="text-xs text-slate-500 mt-1 max-w-md mx-auto">{description}</p>
      {cta && <div className="mt-4 inline-flex">{cta}</div>}
    </div>
  );
}
